import { useConfirm } from "components/ConfirmDialog";
import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import { useCompaniesApi } from "hooks/useCompaniesApi";
import {
  useCompanyGroupApi,
  useCompanyGroupMutations,
  useGroupMemberCompaniesApi,
} from "hooks/useCompanyGroupsApi";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toastError } from "utils/toast-utils";
import { BlacklistFields } from "./BlacklistFields";

/**
 * 企業グループ詳細画面 (ISSUE-803 Phase 1C)。
 *
 * メンバー企業の追加・削除を行う。
 * @return {JSX.Element} 詳細画面
 */
export const CompanyGroupDetailPage: FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: group,
    loading: groupLoading,
    error: groupError,
    refetch: refetchGroup,
  } = useCompanyGroupApi({ id });
  const {
    data: members,
    loading: membersLoading,
    refetch: refetchMembers,
  } = useGroupMemberCompaniesApi({ groupId: id });
  const {
    addCompany,
    removeCompany,
    updateGroup,
    loading: mutating,
  } = useCompanyGroupMutations();
  const { confirm, dialog: confirmDialog } = useConfirm();

  // ブラックリスト設定 (早期 return より前に置く。group はこの時点で undefined の可能性がある)
  const [blacklistDraft, setBlacklistDraft] = useState<{
    isBlacklisted: boolean;
    reason: string;
  }>({ isBlacklisted: false, reason: "" });

  // 同期済みのグループ id。``group`` は refetch のたびに参照が変わるため、
  // 参照ではなく id を見て「別のグループを開いたときだけ」draft を作り直す。
  // これをしないと企業の追加・削除の refetch で未保存の入力が黙って消える。
  // 保存直後だけは意図的にサーバ値へ寄せたいので、null を入れて次の group で再同期させる。
  const syncedGroupIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!group) return;
    if (syncedGroupIdRef.current === group.id) return;
    syncedGroupIdRef.current = group.id;
    setBlacklistDraft({
      isBlacklisted: group.isBlacklisted,
      reason: group.blacklistReason ?? "",
    });
  }, [group]);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const { data: companies, loading: companiesLoading } = useCompaniesApi({
    search: search || undefined,
    limit: 20,
    skip: !search,
  });

  const memberIds = useMemo(
    () => new Set(members?.items.map(m => m.id) ?? []),
    [members],
  );

  if (groupLoading && !group) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <Loading size="md" />
      </div>
    );
  }

  if (groupError || !group) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="alert alert-error">
          <span>
            エラー: {groupError?.message ?? "グループが見つかりません"}
          </span>
        </div>
        <div className="mt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/settings/company-groups")}
          >
            ← 一覧に戻る
          </Button>
        </div>
      </div>
    );
  }

  const handleAdd = async (companyId: string) => {
    try {
      await addCompany(group.id, companyId);
      await refetchMembers();
      await refetchGroup();
    } catch (e) {
      toastError(
        `追加に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleSaveBlacklist = async () => {
    const trimmedReason = blacklistDraft.reason.trim();
    try {
      await updateGroup(group.id, {
        isBlacklisted: blacklistDraft.isBlacklisted,
        // チェックが外れているときは理由欄の中身で分岐する。
        // - 理由が残っている: キーごと送らない (サーバ側は据え置き = 再チェック時に復活)
        // - 理由が空:         null を送ってクリアする ("" は 422 になるので必ず null)
        ...(blacklistDraft.isBlacklisted
          ? { blacklistReason: trimmedReason || null }
          : trimmedReason
            ? {}
            : { blacklistReason: null }),
      });
      // 保存後はサーバ値と同期し直す (チェックを外したときの理由の据え置きなど、
      // サーバ側の判断が draft に反映される)
      syncedGroupIdRef.current = null;
      await refetchGroup();
    } catch (e) {
      toastError(
        `更新に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleRemove = async (companyId: string, name: string) => {
    const ok = await confirm({
      title: "企業をグループから外す",
      message: `"${name}" をこのグループから外しますか?`,
      confirmText: "外す",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await removeCompany(group.id, companyId);
      await refetchMembers();
      await refetchGroup();
    } catch (e) {
      toastError(
        `削除に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/settings/company-groups")}
        >
          ← 企業グループ一覧
        </Button>
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-2">
          {group.isBlacklisted && (
            <StatusBadge className="mb-4" size="xs" tone="tag-danger">
              ブラックリスト
            </StatusBadge>
          )}
          <PageTitle>{group.name}</PageTitle>
        </div>
        {group.description && (
          <p className="text-sm text-base-content/60 mt-1">
            {group.description}
          </p>
        )}
      </div>

      {/* ブラックリスト設定 */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold mb-3">ブラックリスト設定</h2>
        <BlacklistFields
          isBlacklisted={blacklistDraft.isBlacklisted}
          reason={blacklistDraft.reason}
          onIsBlacklistedChange={isBlacklisted =>
            setBlacklistDraft(s => ({ ...s, isBlacklisted }))
          }
          onReasonChange={reason => setBlacklistDraft(s => ({ ...s, reason }))}
          reasonActionSlot={
            <Button
              type="button"
              size="sm"
              onClick={handleSaveBlacklist}
              disabled={mutating}
            >
              保存
            </Button>
          }
        />
      </section>

      {/* メンバー企業一覧 */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold mb-3">所属企業</h2>
        {membersLoading ? (
          <Loading size="sm" />
        ) : (members?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-base-content/60">
            まだ企業が追加されていません
          </p>
        ) : (
          <Table.Root density="compact">
            <Table.Head>
              <tr>
                <Table.HeaderCell>企業名</Table.HeaderCell>
                <Table.HeaderCell>主ドメイン</Table.HeaderCell>
                <Table.HeaderCell>連絡先数</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {members?.items.map(m => (
                <Table.Row key={m.id}>
                  <Table.Cell>
                    <a
                      href={`/settings/companies/${m.id}`}
                      className="link link-primary"
                    >
                      {m.name}
                    </a>
                  </Table.Cell>
                  <Table.Cell>{m.primaryDomain ?? "—"}</Table.Cell>
                  <Table.Cell>
                    <StatusBadge tone="soft-neutral">
                      {m.contactCount}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      onClick={() => handleRemove(m.id, m.name)}
                      disabled={mutating}
                    >
                      外す
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </section>

      {/* 企業追加 */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold mb-3">企業を追加</h2>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            placeholder="企業名で検索..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && setSearch(searchInput.trim())}
            className="input input-bordered input-sm w-64"
          />
          <Button
            type="button"
            size="sm"
            onClick={() => setSearch(searchInput.trim())}
          >
            検索
          </Button>
        </div>
        {!search ? (
          <p className="text-sm text-base-content/60">
            検索キーワードを入力してください
          </p>
        ) : companiesLoading ? (
          <Loading size="sm" />
        ) : (companies?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-base-content/60">
            該当する企業が見つかりません
          </p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {companies?.items.map(c => {
              const isMember = memberIds.has(c.id);
              return (
                <li
                  key={c.id}
                  className="flex items-center justify-between py-2"
                >
                  <div>
                    <div className="text-sm">{c.name}</div>
                    {c.primaryDomain && (
                      <div className="text-xs text-base-content/60">
                        {c.primaryDomain}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="xs"
                    variant={isMember ? "ghost" : "default"}
                    disabled={isMember || mutating}
                    onClick={() => handleAdd(c.id)}
                  >
                    {isMember ? "追加済" : "追加"}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {confirmDialog}
    </div>
  );
};

export default CompanyGroupDetailPage;
